import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import authService from '../../services/auth.service'

interface User {
  id: string
  email: string
  name: string
  role: 'owner' | 'partner' | 'manager'
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  loading: boolean
  error: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      const token = authService.getToken()
      if (token) {
        try {
          // Verify token by fetching current user
          const response = await fetch('http://localhost:8100/api/auth/me', {
            headers: { 'Authorization': `Bearer ${token}` }
          })

          if (response.ok) {
            const userData = await response.json()
            setUser(userData)
          } else {
            authService.logout()
          }
        } catch (error) {
          console.error('Auth check failed:', error)
          authService.logout()
        }
      }
      setLoading(false)
    }

    checkAuth()
  }, [])

  const login = async (email: string, password: string) => {
    setError(null)
    setLoading(true)

    try {
      const { user } = await authService.login(email, password)
      setUser(user)
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Login failed')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    authService.logout()
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, error }}>
      {children}
    </AuthContext.Provider>
  )
}